import { useEffect, useState } from 'react';
import { LoaderCircleIcon } from 'lucide-react';
import { useToast } from '@shadcn/hooks/use-toast';
import { useParams } from 'react-router-dom';
import { EnergyOperations } from '../../components/service/EnergyOperations';
import projectServices from 'app/services/project-services';
import { NoDataFound } from 'app/components/project/NoDataFound';
import { Documents } from 'app/types/visualizations';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shadcn/ui';

export const PortfolioDetail = () => {
  const { name } = useParams();
  const [documents, setDocuments] = useState<Documents>();
  const [meterReadings, setMeterReadings] = useState<Documents>();
  const [portfolioProjectsInsights, setPortfolioProjectsInsights] = useState<any>();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const { toast } = useToast();
  const dataSource = 'energy-star';

  // Years in descending order from 2025 to 2021
  const years = [2025, 2024, 2023, 2022, 2021];

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    const fetchPortfolioInsights = async () => {
      try {
        const data = await projectServices.getPortfolioInsights({ search: name, year: selectedYear });
        if (isMounted) {
          setMeterReadings(data.portfolioInsights.formattedMeterReadings);
          setPortfolioProjectsInsights(data.projectsWithInsights);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error fetching portfolio insights:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch portfolio data',
          variant: 'destructive',
        });
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchPortfolioInsights();

    return () => {
      isMounted = false;
    };
  }, [name, selectedYear, toast]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoaderCircleIcon className="animate-spin" />
      </div>
    );
  }

  if (!dataSource) {
    return <NoDataFound />;
  }

  if (dataSource === 'energy-star' && !meterReadings) {
    return <NoDataFound />;
  }

  return (
    <div className="h-full w-full overflow-auto p-6">
      <div className="mb-4">
        <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(Number(value))}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select Year" />
          </SelectTrigger>
          <SelectContent>
            {years.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <EnergyOperations
        isPortfolio={true}
        year={selectedYear}
        dataSource={dataSource}
        documents={documents as Documents}
        meterReadings={meterReadings as Documents}
        portfolioProjectsInsights={portfolioProjectsInsights}
      />
    </div>
  );
};
